import { useNavigate, useLocation } from 'react-router-dom'
import { Zap, PlayCircle, User, FolderOpen } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useProjectContext } from '@/lib/context/ProjectContext'

interface ExecutionsLayoutProps {
    children: React.ReactNode
}

export default function ExecutionsLayout({ children }: ExecutionsLayoutProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { selectedProject } = useProjectContext()

    const getActiveTab = () => {
        if (location.pathname.includes('/executions/test-runs')) return 'test-runs'
        if (location.pathname.includes('/executions/test-executions')) return 'test-executions'
        if (location.pathname.includes('/executions/my-executions')) return 'my-executions'
        return 'test-runs'
    }

    const handleTabChange = (value: string) => {
        navigate(`/executions/${value}`)
    }

    return (
        <div className="p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Execution Ops</h1>
                <p className="text-muted-foreground mt-1">
                    {selectedProject ? (
                        <>
                            Test runs and execution history for{' '}
                            <span className="font-medium text-foreground">{selectedProject.name}</span>
                        </>
                    ) : (
                        'Manage test runs, executions, and view your execution history'
                    )}
                </p>
            </div>

            <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full max-w-3xl grid-cols-3">
                    <TabsTrigger value="test-runs">
                        <Zap className="h-4 w-4 mr-2" />
                        Test Runs
                    </TabsTrigger>
                    <TabsTrigger value="test-executions">
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Test Executions
                    </TabsTrigger>
                    <TabsTrigger value="my-executions">
                        <User className="h-4 w-4 mr-2" />
                        My Executions
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {!selectedProject ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20">
                    <FolderOpen className="h-12 w-12 text-gray-300" />
                    <h3 className="mt-4 text-lg font-semibold text-gray-600">No Project Selected</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Select a project from the header to continue.
                    </p>
                </div>
            ) : (
                children
            )}
        </div>
    )
}